<template>
	<form
		class="space-y-5"
		novalidate
		@submit.prevent="submit"
	>
		<div class="space-y-1.5">
			<Label for="first-run-target-roles">Target Roles</Label>
			<TagsInput
				id="first-run-target-roles"
				v-model="form.target_roles"
				:max="MAX_TARGET_ROLES"
				:add-on-blur="true"
				:duplicate="false"
			>
				<TagsInputItem
					v-for="role in form.target_roles"
					:key="role"
					:value="role"
				>
					<TagsInputItemText />
					<TagsInputItemDelete />
				</TagsInputItem>
				<TagsInputInput placeholder="Target Role..." />
			</TagsInput>
			<p class="text-xs text-muted-foreground">
				Select up to {{ MAX_TARGET_ROLES }} target roles
			</p>
		</div>

		<div class="space-y-1.5">
			<Label for="first-run-experience">Experience</Label>
			<Select v-model="form.experience_bucket">
				<SelectTrigger
					id="first-run-experience"
					class="h-10 w-full"
				>
					<SelectValue placeholder="Select years of experience" />
				</SelectTrigger>
				<!-- Portals to <body>, so it must outrank the legacy Modal (z-9998) it can render in. -->
				<SelectContent class="z-[10000]">
					<SelectItem
						v-for="bucket in EXPERIENCE_BUCKETS"
						:key="bucket.value"
						:value="bucket.value"
					>
						{{ bucket.label }}
					</SelectItem>
				</SelectContent>
			</Select>
		</div>

		<div class="space-y-1.5">
			<Label>Work Settings</Label>
			<ToggleGroup
				v-model="form.work_settings"
				type="multiple"
				variant="outline"
				:spacing="2"
				class="justify-start"
			>
				<ToggleGroupItem
					v-for="option in WORK_SETTINGS"
					:key="option.value"
					v-slot="{ pressed }"
					:value="option.value"
					class="h-11 min-w-28 justify-center gap-2 px-5 data-[state=on]:bg-won-green-light/40 data-[state=on]:text-won-blue"
				>
					<span>{{ option.label }}</span>
					<Check
						v-if="pressed"
						class="size-4 text-won-green"
						aria-hidden="true"
					/>
				</ToggleGroupItem>
			</ToggleGroup>
			<p class="flex items-center gap-1 text-xs text-yellow-600">
				<TriangleAlert
					class="size-3.5 shrink-0"
					aria-hidden="true"
				/>
				Currently available within the US only
			</p>
		</div>

		<div class="space-y-1.5">
			<Label for="first-run-locations">Location</Label>
			<TagsInput
				id="first-run-locations"
				v-model="form.locations"
				:duplicate="false"
				:add-on-blur="true"
			>
				<TagsInputItem
					v-for="location in form.locations"
					:key="location"
					:value="location"
				>
					<TagsInputItemText />
					<TagsInputItemDelete />
				</TagsInputItem>
				<TagsInputInput placeholder="Add preferred locations..." />
			</TagsInput>
			<p class="text-xs text-muted-foreground">
				If nothing is selected, we'll search across all US locations
			</p>
		</div>

		<div class="space-y-3">
			<div class="flex items-baseline justify-between">
				<Label for="first-run-min-salary">Minimum Annual Salary</Label>
				<span class="text-sm font-medium text-won-green">{{ salaryDisplay }}</span>
			</div>
			<Slider
				id="first-run-min-salary"
				v-model="salarySliderValue"
				:min="SALARY_MIN"
				:max="SALARY_MAX"
				:step="SALARY_STEP"
			/>
		</div>

		<Button
			type="submit"
			class="h-11 w-full bg-won-green text-white hover:bg-won-green/90"
			:disabled="isSubmitDisabled"
		>
			<Loader2
				v-if="processing"
				class="mr-2 size-4 animate-spin"
				aria-hidden="true"
			/>
			{{ processing ? 'Finding jobs…' : submitLabel }}
		</Button>
	</form>
</template>

<script setup>
import { computed, reactive, ref } from 'vue';

import { Check, Loader2, TriangleAlert } from 'lucide-vue-next';

import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '../../components/ui/select';
import { Slider } from '../../components/ui/slider';
import {
	TagsInput,
	TagsInputInput,
	TagsInputItem,
	TagsInputItemDelete,
	TagsInputItemText,
} from '../../components/ui/tags-input';
import { ToggleGroup, ToggleGroupItem } from '../../components/ui/toggle-group';

import { setAuthState } from '../../composables/auth';
import { submitJobSearchProfile } from '../../api/profile.js';
import { useNotifications } from '../../store/notifications.store.js';
import {
	DEFAULT_SALARY,
	EXPERIENCE_BUCKETS,
	MAX_TARGET_ROLES,
	SALARY_MAX,
	SALARY_MIN,
	SALARY_STEP,
	WORK_SETTINGS,
	matchExperienceBucket,
} from '../../constants/job-preferences';

const { setUser } = setAuthState();
const notifications = useNotifications();

const emit = defineEmits(['saved']);
const props = defineProps({
	user: {
		type: Object,
		default: () => ({}),
	},
	/**
	 * Resume-derived defaults ({ titles, min_experience, min_salary }) for a user with no saved
	 * preferences, mirroring how the onboarding Questions step prefills from the resume.
	 */
	recommendations: {
		type: Object,
		default: null,
	},
	submitLabel: {
		type: String,
		default: 'Apply & See Jobs',
	},
});

const settings = props.user?.job_board_setting ?? {};
const options = props.user?.options ?? {};
const recommendations = props.recommendations ?? {};

const clampSalary = (value) => {
	const numeric = Number(value);
	if (Number.isNaN(numeric)) {
		return DEFAULT_SALARY;
	}
	return Math.min(Math.max(numeric, SALARY_MIN), SALARY_MAX);
};

const savedRoles = settings.desired_role_options?.length
	? settings.desired_role_options
	: [settings.desired_role].filter(Boolean);
const initialRoles = (savedRoles.length ? savedRoles : recommendations.titles ?? []).slice(0, MAX_TARGET_ROLES);

// With recommendations the form starts like the onboarding Questions step: every setting on.
const initialWorkSettings = settings.work_settings_options
	?? (props.recommendations ? WORK_SETTINGS.map((option) => option.value) : []);

const form = reactive({
	target_roles: [...initialRoles],
	experience_bucket: matchExperienceBucket(options.experience_years ?? recommendations.min_experience),
	work_settings: [...initialWorkSettings],
	locations: [...(options.preferred_locations ?? [])],
	min_salary: clampSalary(settings.min_salary ?? recommendations.min_salary ?? DEFAULT_SALARY),
});

const processing = ref(false);

const salarySliderValue = computed({
	get: () => [form.min_salary ?? DEFAULT_SALARY],
	set: ([next]) => {
		form.min_salary = clampSalary(next);
	},
});

const salaryFormatter = new Intl.NumberFormat('en-US', {
	style: 'currency',
	currency: 'USD',
	maximumFractionDigits: 0,
});

const salaryDisplay = computed(() => {
	const formatted = salaryFormatter.format(form.min_salary ?? 0);
	return form.min_salary >= SALARY_MAX ? `${formatted}+/yr` : `${formatted}/yr`;
});

const isSubmitDisabled = computed(
	() => processing.value
		|| form.target_roles.length === 0
		|| form.work_settings.length === 0
		|| !form.experience_bucket,
);

const experienceYearsFromBucket = (bucketValue) => {
	const bucket = EXPERIENCE_BUCKETS.find((item) => item.value === bucketValue);
	return bucket?.minYears ?? 0;
};

const submit = async () => {
	if (isSubmitDisabled.value) {
		return;
	}

	processing.value = true;

	try {
		const result = await submitJobSearchProfile({
			target_roles: form.target_roles,
			work_settings: form.work_settings,
			locations: form.locations,
			min_salary: form.min_salary,
			pay_period: 'yearly',
			experience_years: experienceYearsFromBucket(form.experience_bucket),
		});

		if (result?.data) {
			setUser(result.data);
		}

		emit('saved');
	} catch {
		notifications.notify('Something went wrong saving your preferences. Please try again.', 'error');
	} finally {
		processing.value = false;
	}
};
</script>
